import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Table, Alert } from 'react-bootstrap';
import { API_URL } from '../../../utils/constants';
import './AgentsFindScreen.css';

const AgentsFindScreen = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      setError('Please enter a search term');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/agents/search/${encodeURIComponent(searchTerm)}`);
      if (!response.ok) {
        throw new Error('Failed to search agents');
      }
      
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Search error:', error);
      setError('Error searching agents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setSearchResults([]);
    setError('');
  };

  return (
    <Container fluid className="agents-find-screen">
      <Row className="mb-4">
        <Col>
          <h2>Find Agents</h2>
          <p className="text-muted">Search for agents by number, name, city, or type</p>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <Form onSubmit={handleSearch}>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search agents by number, name, city, or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button type="submit" variant="primary" size="sm" style={{ minWidth: 'fit-content' }}>
                Search
              </Button>
            </div>
          </Form>
        </Col>
        <Col md={6} className="text-end">
          <Button variant="outline-secondary" onClick={handleClear} disabled={!searchTerm && searchResults.length === 0}>
            Clear
          </Button>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {loading && (
        <Row className="mb-3">
          <Col>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {searchResults.length > 0 && (
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Search Results ({searchResults.length})</h5>
              </Card.Header>
              <Card.Body>
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Agent #</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>City</th>
                      <th>State</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((agent) => (
                      <tr key={agent.id}>
                        <td>
                          <strong>{agent.agent_number}</strong>
                        </td>
                        <td>{agent.name}</td>
                        <td>{agent.agent_type_name || 'N/A'}</td>
                        <td>{agent.city || 'N/A'}</td>
                        <td>{agent.state || 'N/A'}</td>
                        <td>
                          <span className={`badge ${agent.active ? 'bg-success' : 'bg-danger'}`}>
                            {agent.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {searchResults.length === 0 && !loading && searchTerm && (
        <Row>
          <Col>
            <Alert variant="info">
              No agents found matching your search criteria.
            </Alert>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default AgentsFindScreen;
